import { injectable } from '@servicetitan/react-ioc';
import { CancelToken } from 'axios';

import { HttpClient } from './http-client';

export interface TechnicianPermissionsResponse {
    permissions?: number[];
}

export interface FieldAssistProEnabledResponse {
    isEnabled: boolean;
}

export interface DemoMcciAvailabilityResponse {
    enabled: boolean;
}

export interface MidCallReportModel {
    markdownContent?: string;
    version: number;
    // ISO-8601 last-generated time (BFF coalesces salespro updatedAt ?? generatedAt); used to decide if it is stale on open.
    updatedAt?: string;
}

@injectable()
export class TechniciansApi {
    constructor(private readonly httpClient: HttpClient) {}

    getTechnicianPermissions = (cancellationToken?: CancelToken) => {
        return this.httpClient.get<TechnicianPermissionsResponse>(
            `/api/v1/technicians/me/permissions`,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    getFieldAssistProEnabled = (cancellationToken?: CancelToken) => {
        return this.httpClient.get<FieldAssistProEnabledResponse>(
            `/api/v1/technician/field-assist-pro-enabled`,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    // Whether the Field Pro Coaching topic should be shown to the current technician. Backed by the
    // salespro-web (Field Pro 2) microservice via the BFF (FE -> BFF -> salespro-web), checking the
    // Enable Field Pro feature gate + FP2 access + ViewFieldPro permission.
    getFieldProTopicAvailability = (cancellationToken?: CancelToken) => {
        return this.httpClient.get<FieldAssistProEnabledResponse>(
            `/api/v1/field-pro/topic-availability`,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    // Demo tenants only, and scoped to one appointment: true when the technician recorded it and
    // the demo library has an active fixture. Never rejects on a demo-side problem; the BFF
    // answers false.
    getDemoMcciAvailability = (appointmentId: number, cancellationToken?: CancelToken) => {
        return this.httpClient.get<DemoMcciAvailabilityResponse>(
            `/api/v1/field-pro/demo-mcci-availability?appointment_id=${appointmentId}`,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    // Mid-call (halftime) report for an appointment, sourced from salespro-web. The backend returns
    // 204 No Content when the report hasn't been generated yet (axios resolves with empty data).
    getMidCallReport = (appointmentId: number, cancellationToken?: CancelToken) => {
        return this.httpClient.get<MidCallReportModel | ''>(
            `/api/v1/field-pro/mid-call-report?appointment_id=${appointmentId}`,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    // Requests an (asynchronous) regeneration of the appointment's mid-call report. Resolves on 202;
    // the refreshed content arrives later, so callers poll getMidCallReport and watch `version`.
    regenerateMidCallReport = (appointmentId: number, cancellationToken?: CancelToken) => {
        return this.httpClient.post<MidCallReportModel | '', undefined>(
            `/api/v1/field-pro/mid-call-report/regenerate?appointment_id=${appointmentId}`,
            undefined,
            {
                cancelToken: cancellationToken,
            }
        );
    };

    // Demo tenants only (see demoMcciEnabled). Returns the report, so nothing polls; 204 = no
    // fixture configured.
    generateDemoMidCallReport = (appointmentId: number, cancellationToken?: CancelToken) => {
        return this.httpClient.post<MidCallReportModel | '', undefined>(
            `/api/v1/field-pro/demo-mcci?appointment_id=${appointmentId}`,
            undefined,
            {
                cancelToken: cancellationToken,
            }
        );
    };
}
